import bugsnagClient from '@getaccept/lib-shared/src/bugsnag';
import type { FontSettings } from '@getaccept/lib-shared/src/fonts/types/font';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { getAuthUrl } from '@getaccept/lib-shared/src/select-entity/select-entity.helper';
import type { SelectEntity } from '@getaccept/lib-shared/src/select-entity/types/select-entity';
import { useToast } from '@getaccept/lib-shared/src/toast';
import type { Entity } from '@getaccept/lib-shared/src/types/entity';
import { computed, ref } from '@vue/composition-api';
import { defineStore } from 'pinia';
import type { Channel } from '@getaccept/pusher';
import type { Addon } from '@getaccept/lib-shared/src/types/addon';
import type { Ref } from '@vue/composition-api';
import { EntityService } from '../api/entity/services/entity.service';
import type { UpdateEntityInput } from '../api/entity/types/update-entity-input';
import { SelectEntityService } from '../api/select-entities/services/select-entities.service';

export const useEntityStore = defineStore('entity', () => {
  const { toast } = useToast();

  const addons = ref<Addon[]>([]);
  const entities = ref<SelectEntity[]>(null);
  const entity = ref<Entity>({
    logoUrl: '',
    theme: {
      name: '',
      accentColor: '',
      backgroundColor: '',
      backgroundImage: '',
      signButtonText: '',
    },
  });
  const pusherChannel: Ref<Channel> = ref();

  const isSaving = ref(false);
  const isSuccess = ref(false);
  const isError = ref(false);
  const isLoadEntityError = ref(false);
  const isLoading = ref(false);
  const isLoaded = ref(false);

  const loadEntity = async () => {
    isLoading.value = true;
    isLoadEntityError.value = false;
    try {
      const data = await EntityService.fetchEntity();
      entity.value = data;
      isLoaded.value = true;
    } catch (e) {
      toast.danger(t('could-not-load-entity'));
      console.error('Could not load entity');
      isLoadEntityError.value = true;
    } finally {
      isLoading.value = false;
    }
  };

  const loadAddons = async () => {
    try {
      const data = await EntityService.fetchAddons();
      addons.value = data;
    } catch (e) {
      toast.danger(t('could-not-load-addons'));
    }
  };

  const loadEntities = async () => {
    try {
      const data = await SelectEntityService.entities();
      entities.value = data;
    } catch (e) {
      if (bugsnagClient) {
        bugsnagClient.notify(e);
      }
      toast.danger(t('could-not-load-entities'));
    }
  };

  const selectEntity = async (entityId: string) => {
    try {
      await SelectEntityService.selectEntity(entityId);
      window.open('/', '_self');
    } catch (e) {
      if (bugsnagClient) {
        bugsnagClient.notify(e);
      }
      toast.danger(t('could-not-load-the-selected-entity'));
    }
  };

  const authEntity = ({ id, auth }: SelectEntity) => {
    window.open(getAuthUrl(id, auth), '_self');
  };

  const setEntity = (newEntity: Entity) => {
    entity.value = newEntity;
  };
  const updateEntity = async (payload: UpdateEntityInput) => {
    try {
      isSaving.value = true;
      await EntityService.update(payload);
      loadEntity();
      isSuccess.value = true;
      setTimeout(() => (isSuccess.value = false), 1500);
    } catch (e) {
      isError.value = true;
      if (bugsnagClient) {
        bugsnagClient.notify(e);
      }
    } finally {
      isSaving.value = false;
    }
  };
  const setEntityThemeFontSettings = (fontSettings: FontSettings) => {
    entity.value.theme.fontSettings = fontSettings;
  };

  const entityId = computed(() => entity.value?.id);

  return {
    addons,
    loadAddons,
    entityId,
    entity,
    loadEntity,
    entities,
    loadEntities,
    selectEntity,
    authEntity,
    setEntity,
    updateEntity,
    isSaving,
    isSuccess,
    isError,
    isLoadEntityError,
    setEntityThemeFontSettings,
    pusherChannel,
    isLoading,
    isLoaded,
  };
});
